import { useEffect, useRef, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// Module-level tracking for skip functionality (shared across hook instances)
const recentlyModifiedHerds = new Set<string>();
const herdTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export function skipRealtimeForHerd(herdId: string, durationMs = 5000) {
  console.log("[useHerdRealtimeUpdates] Skipping realtime for herd:", herdId, "for", durationMs, "ms");
  
  // Clear existing timeout if any
  const existingTimeout = herdTimeouts.get(herdId);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }
  
  recentlyModifiedHerds.add(herdId);
  
  const timeout = setTimeout(() => {
    console.log("[useHerdRealtimeUpdates] Re-enabling realtime for herd:", herdId);
    recentlyModifiedHerds.delete(herdId);
    herdTimeouts.delete(herdId);
  }, durationMs);
  
  herdTimeouts.set(herdId, timeout);
}

// Debounce utility
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return ((...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  }) as T;
}

export function useHerdRealtimeUpdates() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Create debounced invalidate function
  const debouncedInvalidate = useMemo(
    () => debounce((queryKey: string[]) => {
      queryClient.invalidateQueries({ queryKey });
    }, 300),
    [queryClient]
  );

  useEffect(() => {
    console.log('[useHerdRealtimeUpdates] Setting up realtime subscription');
    
    const channel = supabase
      .channel("herd-member-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "herd_members",
        },
        (payload) => {
          const newRecord = payload.new as { herd_id?: string; user_id?: string } | null;
          const oldRecord = payload.old as { herd_id?: string; user_id?: string } | null;
          const herdId = newRecord?.herd_id || oldRecord?.herd_id;
          const affectedUserId = newRecord?.user_id || oldRecord?.user_id;
          
          // Skip if this herd was recently modified locally (prevents race condition)
          if (herdId && recentlyModifiedHerds.has(herdId)) {
            console.log('[useHerdRealtimeUpdates] Skipping update for recently modified herd:', herdId);
            return;
          }
          
          // Skip invalidating current user's queries - they have optimistic state
          if (affectedUserId === user?.id) {
            console.log('[useHerdRealtimeUpdates] Skipping update for current user - using optimistic state');
            return;
          }
          
          console.log('[useHerdRealtimeUpdates] Herd member change:', payload.eventType, herdId, 'user:', affectedUserId);
          
          if (herdId) {
            // Debounced invalidation to prevent rapid refetches
            debouncedInvalidate(["herd", herdId]);
            debouncedInvalidate(["herd-members", herdId]);
            queryClient.invalidateQueries({ queryKey: ["is-herd-member", herdId], exact: false });
          }
          
          // Invalidate user-herds for the affected user (only if not current user)
          if (affectedUserId) {
            debouncedInvalidate(["user-herds", affectedUserId]);
          }
          
          // Debounced invalidation for all herds list
          debouncedInvalidate(["all-herds"]);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "herds",
        },
        (payload) => {
          const updatedHerd = payload.new as { id: string; member_count: number };
          
          // Skip if this herd was recently modified locally
          if (recentlyModifiedHerds.has(updatedHerd.id)) {
            return;
          }
          
          // Update specific herd in cache when member_count changes
          queryClient.setQueryData(["herd", updatedHerd.id], (old: any) => {
            if (old) {
              return { ...old, member_count: updatedHerd.member_count };
            }
            return old;
          });
          
          // Update in all-herds list
          queryClient.setQueryData(["all-herds"], (old: any[] | undefined) => {
            if (old) {
              return old.map((herd) =>
                herd.id === updatedHerd.id
                  ? { ...herd, member_count: updatedHerd.member_count }
                  : herd
              );
            }
            return old;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, user?.id, debouncedInvalidate]);
}
